import { myFirebase } from '@/config/firebaseConfig';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { collection, query, where, onSnapshot, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { uuid } from '@/utils';
import { isDevelopment } from '@/utils/environment';

import { useAuthContext } from '@/contexts/authContext';

import { Client, ClientStatus, ExportedClient, Gender, Session, SessionCreationData, SessionStatus } from '@/models';

const functions = getFunctions(myFirebase);

interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
}

class UserService {
  private auth = myFirebase.auth();
  private firestore = myFirebase.firestore();

  public getClients = (userUID: string, callback: (clients: any[]) => void) => {
    const clientsRef = collection(this.firestore, 'clients');
    const q = query(clientsRef, where('therapistId', '==', userUID), where('status', '==', 'Active'));

    return onSnapshot(q, (querySnapshot) => {
      const clients = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      callback(clients);
    });
  };

  public getORYXGOClients = (
    userUID: string,
    callback: (clients: ExportedClient[], loading: boolean, error: string | null) => void,
  ) => {
    console.log('Fetching ORYXGO Clients for user:', userUID);

    const clientsRef = collection(this.firestore, 'clients');
    const q = query(clientsRef, where('therapistId', '==', userUID), where('status', '==', 'Active'));

    let loading = true;
    let error: string | null = null;

    callback([], loading, error); // Notify frontend that data is loading

    return onSnapshot(
      q,
      async (querySnapshot) => {
        try {
          const clients = querySnapshot.docs.map(
            (doc): ExportedClient => ({
              firstName: doc.data().firstName,
              lastName: doc.data().lastName,
              dateOfBirth: doc.data().dateOfBirth,
              lastSession: doc.data().lastSession,
              email: doc.data().email,
              gender: doc.data().gender,
              status: doc.data().status,
              orgId: doc.data().orgId,
              therapistId: doc.data().therapistId,
              createdAt: doc.data().createdAt,
              totalSessions: doc.data().totalSessions,
              id: doc.id,
              role: doc.data().role,
            }),
          );

          console.log('Total Clients Retrieved:', clients.length);

          const processedClients = await Promise.all(
            clients.map(async (client) => {
              console.log('Processing client:', client.id);

              const sessionsRef = collection(this.firestore, 'sessions');
              const sessionQuery = query(
                sessionsRef,
                where('clientId', '==', client.id),
                where('therapistId', '==', userUID),
                where('status', '==', 'Active'),
                orderBy('date', 'desc'),
              );

              const sessionSnapshot = await getDocs(sessionQuery);
              console.log('Total Sessions Retrieved for client:', client.id, sessionSnapshot.docs.length);

              const sessions = sessionSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              })) as Session[];

              // Step 1: Collect all report IDs
              const reportIds = new Set<string>();
              sessions.forEach((session) => {
                if (session.reports && Array.isArray(session.reports)) {
                  session.reports.forEach((reportId) => reportIds.add(reportId));
                }
              });

              if (reportIds.size === 0) {
                console.log('No reports found in any session for client:', client.id);
                return null; // Exclude this client
              }

              console.log('Total unique reports found:', reportIds.size);

              // Step 2: Fetch all report documents
              const reportDocs = await Promise.all(
                Array.from(reportIds).map((reportId) =>
                  getDoc(doc(this.firestore, `users/${userUID}/reportInfo`, reportId)),
                ),
              );

              // Step 3: Identify reports that are ORYX Movement Analysis
              const oryxReportIds = new Set<string>();
              reportDocs.forEach((reportDoc) => {
                if (reportDoc.exists()) {
                  const reportData = reportDoc.data();
                  if (reportData.measurementType === 'ORYX Movement Analysis') {
                    oryxReportIds.add(reportDoc.id);
                  }
                }
              });

              console.log('Total ORYX Movement Analysis reports:', oryxReportIds.size);

              // Step 4: Filter sessions to only include those with ORYX reports
              const oryxSessions = sessions.filter((session) =>
                session.reports?.some((reportId) => oryxReportIds.has(reportId)),
              );

              console.log(
                'Total sessions with ORYX Movement Analysis reports for client:',
                client.id,
                oryxSessions.length,
              );

              if (oryxSessions.length === 0) {
                console.log('No ORYX sessions found for client:', client.id);
                return null; // Exclude this client
              }

              // Step 5: Update client object with ORYX session data
              const updatedClient: ExportedClient = {
                ...client,
                totalSessions: oryxSessions.length,
                lastSession: oryxSessions.length > 0 ? oryxSessions[0].date : client.lastSession,
              };

              return updatedClient;
            }),
          );

          // Remove null values (clients without ORYX sessions)
          const filteredClients = processedClients.filter((client) => client !== null);
          console.log('Filtered Clients with ORYX Sessions:', filteredClients.length);

          loading = false; // Data has finished loading
          callback(filteredClients as ExportedClient[], loading, error);
        } catch (err) {
          console.error('Error fetching ORYXGO clients:', err);
          error = (err as Error).message;
          loading = false;
          callback([], loading, error);
        }
      },
      (err) => {
        console.error('Firestore snapshot error:', err);
        error = err.message;
        loading = false;
        callback([], loading, error);
      },
    );
  };

  public getSessions = (userUID: string, clientID: string, callback: (sessions: any[]) => void) => {
    // TODO: Add info for the reportCard
    const sessionsRef = collection(this.firestore, 'sessions');
    const q = query(
      sessionsRef,
      where('therapistId', '==', userUID),
      where('status', '==', 'Active'),
      where('clientId', '==', clientID),
      orderBy('date', 'desc'),
    );

    return onSnapshot(q, (querySnapshot) => {
      const sessions = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // callback(sessions);
      const reportIds = new Set<string>();
      sessions.forEach((session: any) => {
        if (session.reports && Array.isArray(session.reports)) {
          session.reports.forEach((reportId: string) => reportIds.add(reportId));
        }
      });

      if (reportIds.size === 0) {
        // No reports found, return sessions as is
        callback(sessions);
        return;
      }
    });
  };

  public async addClient(clientData: CreateUserData): Promise<string> {
    try {
      // Get Firebase Callable Function
      const createClient = httpsCallable(functions, 'createClient');

      // Call the function
      const result = await createClient({
        email: clientData.email,
        firstName: clientData.firstName,
        lastName: clientData.lastName,
        dateOfBirth: clientData.dateOfBirth,
      });

      console.log('Client created successfully:', result.data);
      const data = result.data as { uid: string };
      return data.uid; // Return the newly created client's UID
    } catch (error: any) {
      if (error.code) {
        switch (error.code) {
          case 'functions/unauthenticated':
            throw new Error('You need to log in before creating a client.');
          case 'functions/not-found':
            throw new Error('Your account details could not be found.');
          case 'functions/permission-denied':
            throw new Error('You do not have permission to create a client.');
          case 'functions/invalid-argument':
            throw new Error('Please fill in all required fields correctly.');
          case 'functions/already-exists':
            throw new Error('A user with this email already exists.');
          case 'functions/internal':
          default:
            throw new Error('An unexpected error occurred. Please try again later.');
        }
      }

      throw new Error('An unknown error occurred.');
    }
  }

  public async archiveClient(uid: string): Promise<void> {
    try {
      await this.firestore.collection('clients').doc(uid).update({ status: ClientStatus.Archived });
    } catch (error) {
      console.error('Error archiving client:', error);
      throw error;
    }
  }

  async getClient(uid: string): Promise<firebase.firestore.DocumentSnapshot> {
    try {
      const clientDoc = await this.firestore.collection('clients').doc(uid).get();
      return clientDoc;
    } catch (error) {
      console.error('Error getting client:', error);
      throw error;
    }
  }

  async updateClient(uid: string, clientData: any): Promise<void> {
    try {
      await this.firestore.collection('clients').doc(uid).update(clientData);
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  }

  async deleteClient(uid: string): Promise<void> {
    try {
      await this.auth.currentUser?.delete();
      await this.firestore.collection('clients').doc(uid).delete();
    } catch (error) {
      console.error('Error deleting client:', error);
      throw error;
    }
  }

  public async createSession(sessionData: SessionCreationData): Promise<string> {
    try {
      const sessionRef = this.firestore.collection('sessions').doc();
      // TODO: change the date to admin.firestore.Timestamp.fromDate(new Date(date))
      const session = {
        ...sessionData,
        id: sessionRef.id,
        status: 'Active',
        _mutationHistory: [
          {
            mutationType: 'create',
            mutationDate: firebase.firestore.Timestamp.fromDate(new Date()),
            mutationBy: sessionData.therapistId,
          },
        ],
      };

      await sessionRef.set(session);
      return sessionRef.id;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  public async moveFileToReports(therapistID: string, sessionId: string, fileId: string): Promise<void> {
    try {
      // Add report Reference to the sessionFile
      const mutation_history = {
        mutationDate: firebase.firestore.Timestamp.fromDate(new Date()),
        updatedFields: 'reports',
        mutationType: 'update',
        mutationBy: therapistID,
      };
      const sessionRef = this.firestore.collection('sessions').doc(sessionId);
      const currentReports = (await sessionRef.get()).get('reports') as string[];
      const reports = currentReports ? [...currentReports, fileId] : [fileId];
      sessionRef
        .update({ reports: reports, _mutationHistory: firebase.firestore.FieldValue.arrayUnion(mutation_history) })
        .then((_doc) => {
          if (import.meta.env.MODE !== 'production') console.log('Added reports!');
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      console.error('Error moving file to reports:', error);
      throw error;
    }

    // TODO: Copy the contents of the reports including all its files to the reports section of the db
    // try {
    //   const reportInfoRef = this.firestore.collection('users').doc(therapistID).collection('reportInfo').doc(fileId);
    //   const reportInfo = await reportInfoRef
    //     .get()
    //     .then((doc) => {
    //       if (doc.exists) {
    //         if (import.meta.env.MODE !== 'production') console.log('Document data:', doc.data());
    //         return doc.data();
    //       } else {
    //         console.log('No such document!');
    //       }
    //     })
    //     .catch((error) => {
    //       console.log('Error getting document:', error);
    //     });

    //   if (reportInfo) {
    //     // Copy the reportInfo to the reports section
    //     const reportRef = this.firestore.collection('reports').collection('reportInfo').doc(fileId);
    //     await reportRef.set(reportInfo);
    //   }
    // } catch (error) {
    //   console.error('Error getting reportInfo:', error);
    //   throw error;
    // }
  }
}

// eslint-disable-next-line import/no-default-export
export default new UserService();
